import React, { useState, useEffect, useRef } from "react";
import { Box, TextField, Button, Typography, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import InputAdornment from "@mui/material/InputAdornment";
import { alpha } from "@mui/material/styles";

export default function DateRangeSelector({
  departureDate,
  returnDate,
  setDepartureDate,
  setReturnDate,
  label = "Date Range",
  errorStart,
  errorEnd,
  searchType,
  flightType,
  splitInputs = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activeField, setActiveField] = useState("departure"); // or "return"
  const departureFieldRef = useRef(null);
  const returnFieldRef = useRef(null);
  const datepickerRef = useRef(null);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });

  const formatDate = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const formatDisplayDate = (date) =>
    date
      ? date.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        })
      : "";

  useEffect(() => {
    if (isMobile) return; // Dialog handles its own close on mobile
    const handleClickOutside = (e) => {
      if (datepickerRef.current && !datepickerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isMobile]);

  const handleDayClick = (dayStr) => {
    const [year, month, day] = dayStr.split("-").map(Number);
    const selectedDate = new Date(year, month - 1, day);
    if (activeField === "departure") {
      setDepartureDate(selectedDate);
      if (returnDate && selectedDate > returnDate) {
        setReturnDate(null);
      }
      if (!returnDate) {
        setActiveField("return");
      }
      return;
    }

    if (activeField === "return") {
      if (!departureDate || selectedDate < departureDate) {
        setDepartureDate(selectedDate);
        setReturnDate(null);
        setActiveField("return");
      } else {
        setReturnDate(selectedDate);
        setActiveField(null);
      }
      return;
    }

    if (!departureDate && !returnDate) {
      setDepartureDate(selectedDate);
      setActiveField("return");
      return;
    }

    if (departureDate && returnDate) {
      setDepartureDate(selectedDate);
      setReturnDate(null);
      setActiveField("return");
      return;
    }
  };

  const updateInput = () => {
    if (departureDate && returnDate)
      return `${formatDate(departureDate)} - ${formatDate(returnDate)}`;
    if (departureDate) return formatDate(departureDate);
    return "";
  };

  const renderCalendar = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<Box key={`e-${i}`} />);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const day = new Date(year, month, i);
      const isPast = day < today;
      const dayStr = formatDate(day);
      const isStart = departureDate && dayStr === formatDate(departureDate);
      const isEnd = returnDate && dayStr === formatDate(returnDate);
      const isBetween =
        departureDate && returnDate && day > departureDate && day < returnDate;

      days.push(
        <Box
          key={i}
          onClick={() => !isPast && handleDayClick(dayStr)}
          sx={{
            width: { xs: 40, sm: 42 },
            height: { xs: 40, sm: 42 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: isStart
              ? "20px 0 0 20px"
              : isEnd
                ? "0 20px 20px 0"
                : isBetween
                  ? 0
                  : "50%",
            backgroundColor:
              isStart || isEnd
                ? "primary.main"
                : isBetween
                  ? alpha(theme.palette.primary.main, 0.3)
                  : "transparent",
            color: isPast
              ? theme.palette.text.disabled
              : isStart || isEnd
                ? "white"
                : theme.palette.text.primary,
            cursor: isPast ? "not-allowed" : "pointer",
            fontWeight: isStart || isEnd ? "bold" : "normal",
            opacity: isPast ? 0.5 : 1,
            fontSize: { xs: "0.95rem", sm: "0.875rem" },
            "&:hover": {
              backgroundColor: isPast
                ? "transparent"
                : theme.palette.primary.light,
              color: isPast ? theme.palette.text.disabled : "white",
            },
          }}
        >
          {i}
        </Box>
      );
    }

    return days;
  };

  const calendarContent = (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 1,
          alignItems: "center",
        }}
      >
        <Button
          onClick={() =>
            setCurrentDate(
              new Date(currentDate.setMonth(currentDate.getMonth() - 1))
            )
          }
        >
          Prev
        </Button>
        <Typography variant="body2">
          {currentDate.toLocaleString("default", { month: "long" })}{" "}
          {currentDate.getFullYear()}
        </Typography>
        <Button
          onClick={() =>
            setCurrentDate(
              new Date(currentDate.setMonth(currentDate.getMonth() + 1))
            )
          }
        >
          Next
        </Button>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0.5,
          justifyItems: "center",
          mb: 2,
        }}
      >
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <Typography key={d} variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {d}
          </Typography>
        ))}
        {renderCalendar()}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button
          size="small"
          onClick={() => {
            setDepartureDate(null);
            setReturnDate(null);
            setIsOpen(false);
          }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => setIsOpen(false)}
        >
          Apply
        </Button>
      </Box>
    </>
  );

  return (
    <Box ref={datepickerRef} sx={{ width: "100%" }}>
      {splitInputs ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 1 },
            width: "100%",
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            inputRef={departureFieldRef}
            label={
              searchType === "staysOnly" ? "Check-in Date" : "Departure Date"
            }
            value={departureDate ? formatDisplayDate(departureDate) : ""}
            onClick={(e) => {
              if (!isMobile) {
                const rect = e.currentTarget.getBoundingClientRect();
                setCalendarPosition({
                  top: rect.bottom + window.scrollY + 4,
                  left: Math.min(
                    rect.left + window.scrollX,
                    window.innerWidth - 320
                  ),
                });
              }
              setActiveField("departure");
              setIsOpen(true);
            }}
            placeholder={searchType === "staysOnly" ? "Check-in" : "Departure"}
            error={!!errorStart}
            helperText={errorStart}
            inputProps={{ readOnly: true, style: { fontSize: "16px" } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarTodayIcon
                    sx={{
                      fontSize: 18,
                      color: theme.palette.text.primary,
                      cursor: "pointer",
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              borderRadius: 1,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.mode === "dark" ? "#666" : "rgba(0,0,0,0.23)",
              },
            }}
          />

          <TextField
            fullWidth
            variant="outlined"
            inputRef={returnFieldRef}
            label={
              searchType === "staysOnly" ||
              (searchType === "flightsAndStays" && flightType === "oneway")
                ? "Check-out Date"
                : "Return Date"
            }
            value={returnDate ? formatDisplayDate(returnDate) : ""}
            onClick={(e) => {
              if (!isMobile) {
                const rect = e.currentTarget.getBoundingClientRect();
                setCalendarPosition({
                  top: rect.bottom + window.scrollY + 4,
                  left: Math.min(
                    rect.left + window.scrollX,
                    window.innerWidth - 320
                  ),
                });
              }
              setActiveField("return");
              setIsOpen(true);
            }}
            placeholder={
              searchType === "staysOnly" ||
              (searchType === "flightsAndStays" && flightType === "oneway")
                ? "Check-out"
                : "Return"
            }
            error={!!errorEnd}
            helperText={errorEnd}
            inputProps={{ readOnly: true, style: { fontSize: "16px" } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarTodayIcon
                    sx={{
                      fontSize: 18,
                      color: theme.palette.text.primary,
                      cursor: "pointer",
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              borderRadius: 1,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.mode === "dark" ? "#666" : "rgba(0,0,0,0.23)",
              },
            }}
          />
        </Box>
      ) : (
        <TextField
          fullWidth
          value={updateInput()}
          onClick={(e) => {
            if (!isMobile) {
              const rect = e.currentTarget.getBoundingClientRect();
              setCalendarPosition({
                top: rect.bottom + window.scrollY + 4,
                left: Math.min(
                  rect.left + window.scrollX,
                  window.innerWidth - 320
                ),
              });
            }
            setIsOpen(!isOpen);
          }}
          placeholder="Select date range"
          inputProps={{ readOnly: true, style: { fontSize: "16px" } }}
        />
      )}

      {/* Mobile: full-screen Dialog */}
      {isMobile ? (
        <Dialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          fullScreen
          PaperProps={{
            sx: {
              bgcolor: theme.palette.background.paper,
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 0,
            }}
          >
            <Typography variant="h6">
              {activeField === "departure"
                ? searchType === "staysOnly"
                  ? "Select Check-in"
                  : "Select Departure"
                : searchType === "staysOnly"
                  ? "Select Check-out"
                  : "Select Return"}
            </Typography>
            <IconButton onClick={() => setIsOpen(false)} edge="end">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {calendarContent}
          </DialogContent>
        </Dialog>
      ) : (
        /* Desktop: absolute dropdown */
        isOpen && (
          <Box
            sx={{
              position: "absolute",
              top: calendarPosition.top,
              left: calendarPosition.left,
              zIndex: 1300,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 2,
              boxShadow: 3,
              minWidth: 300,
            }}
          >
            {calendarContent}
          </Box>
        )
      )}
    </Box>
  );
}
